import {
    Checkbox,
    DefaultButton, IPanelProps,
    IRenderFunction, MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Separator, Stack, TextField, Toggle
} from "@fluentui/react";
import { RootState } from "app/store";
import CustomPanel from "components/controls/CustomPanel";
import { fadeIn, panelButtonsStyles } from "global/Application";
import { useApplication } from "global/Application/Context";
import { addUpdateFeesAndPremiums } from "global/Reducer/dataSlice";
import { IPanelEvent } from "model/Extension";
import {
    IFeesAndPremiums as FAM_IFeesAndPremiums
} from "model/FlexibleAttributeModel";
import { useEffect, useState } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";

interface IData {
    feeAndPremium: FAM_IFeesAndPremiums | null;
}

interface IProps extends TComponentConnectedProps {
    //TODO: unmount component with delay for animation
    isOpen: boolean;

    // cancellation of transaction/partial actions
    onCancel?: () => void;

    // dialog closure request from parent component
    onDismiss: () => void

    // use this prior to completion of primary action for verification/or to perform action on parent
    onConfirm?: (e?: IPanelEvent<IData>) => void;

    // start condition
    data?: IData;
}

const FeesAndPremiumProperties = (props: IProps) => {

    const dispatch = useDispatch();
    const application = useApplication();

    // current view state/stages
    const [validationMessage, setValidationMessage] = useState<string | null>(null);

    // selected category
    const [feeAndPremium, setFeeAndPremium] = useState<FAM_IFeesAndPremiums | null>(null);


    const [cascadeDeactivateFlag, setCascadeDeactivateFlag] = useState<boolean | null>(null);
    const [cascadeDeactivateNonstandard, setCascadeDeactivateNonstandard] = useState<boolean>(false);
    const [cascadeDeactivateAttributeSet, setCascadeDeactivateAttributeSet] = useState<boolean>(false);


    useEffect(() => {
        if (props.isOpen) {
            if (props.data?.feeAndPremium)
                setFeeAndPremium(structuredClone(props.data.feeAndPremium));

            setCascadeDeactivateFlag(null);
            setCascadeDeactivateNonstandard(false);
            setCascadeDeactivateAttributeSet(false);
        }

    }, [props.isOpen]);

    function onRenderAddProperties(): JSX.Element {

        if (!feeAndPremium)
            return <div>Fee and Premium not available!</div>;

        return (
            <div className={fadeIn}>
                <Stack tokens={{ childrenGap: 10 }}>

                    <TextField
                        label='Name'
                        value={feeAndPremium.name ?? ''}
                        onChange={(event, value) => {
                            setFeeAndPremium(t => { return { ...feeAndPremium, name: value ?? '' } });
                        }}
                    />

                    <Toggle
                        label={"Deactivate"}
                        disabled={!application.ChangeTypePermissions.feesAndPremium.modify_deactivate}
                        checked={feeAndPremium.deactivated ?? false}
                        onChange={(_, checked) => {
                            setFeeAndPremium(t => { return { ...feeAndPremium, deactivated: checked ?? false } });
                            setCascadeDeactivateFlag(checked ?? false);
                            setCascadeDeactivateNonstandard(checked ?? false);
                            setCascadeDeactivateAttributeSet(checked ?? false);
                        }}
                    />


                    {cascadeDeactivateFlag != null &&
                        <CustomPanel title="Cascade Deactivation Change" >
                            <Stack tokens={{ childrenGap: 10 }} styles={{ root: { padding: '10px' } }}>
                                <Checkbox
                                    label={`${feeAndPremium.deactivated ? "Deactivate" : "Activate"} all Nonstandards`}
                                    checked={cascadeDeactivateNonstandard}
                                    disabled={!application.ChangeTypePermissions.feesAndPremium.modify_deactivate || feeAndPremium.deactivated}
                                    onChange={(_, checked) => {
                                        setCascadeDeactivateNonstandard(checked ?? false);
                                    }}
                                />

                                <Checkbox
                                    label={`${feeAndPremium.deactivated ? "Deactivate" : "Activate"} all Attributesets`}
                                    checked={cascadeDeactivateAttributeSet}
                                    disabled={!application.ChangeTypePermissions.feesAndPremium.modify_deactivate || feeAndPremium.deactivated}
                                    onChange={(_, checked) => {
                                        setCascadeDeactivateAttributeSet(checked ?? false);
                                    }}
                                />
                            </Stack>

                        </CustomPanel>
                    }

                </Stack>
            </div>
        );
    }

    function onRenderAddPropertiesFooter(): JSX.Element | null {
        if (!feeAndPremium)
            return <div>Plan not available!</div>;

        return <div className={fadeIn}>
            <PrimaryButton text='OK'
                styles={panelButtonsStyles}
                disabled={feeAndPremium.name === ''}
                onClick={() => {

                    // TODO for nonstandard
                    // // cascade deactivation to nonstandards and attribute sets
                    // if (cascadeDeactivateNonstandard)
                    //     feeAndPremium.nonStandards.forEach(eachNonStandard => {
                    //         eachNonStandard.deactivated = feeAndPremium.deactivated;
                    //     });

                    if (cascadeDeactivateAttributeSet)
                        feeAndPremium.attributeSets.forEach(eachAttributeSet => {
                            eachAttributeSet.deactivated = feeAndPremium.deactivated;
                        });


                    dispatch(addUpdateFeesAndPremiums([feeAndPremium]));
                    props.onDismiss?.();
                }}
            />
            <DefaultButton text='Cancel' onClick={props.onDismiss} />
        </div>;
    }

    const onRenderUnknown: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        return (<div>Unknown Status</div>)
    }

    const onRenderBody: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        let onRender = onRenderUnknown;

        onRender = onRenderAddProperties;

        return (
            <div>
                <Separator />
                {validationMessage &&
                    <MessageBar className={fadeIn} messageBarType={MessageBarType.error} onDismiss={() => setValidationMessage("")}                >
                        {validationMessage}
                    </MessageBar>
                }

                {onRender()}
            </div>
        );
    }

    const onRenderFooterContent: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        let onRender = onRenderUnknown;
        onRender = onRenderAddPropertiesFooter;

        return onRender();
    }

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            type={PanelType.medium}
            closeButtonAriaLabel="Close"
            headerText="Properties"
            // onRenderHeader={onRenderHeader}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            {onRenderBody()}
        </Panel>
    )
}


function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(FeesAndPremiumProperties);
